import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, User, MoveRight, Mic, Square, Loader2, AlertTriangle, Video, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PIPELINE_STAGES = [
  { id: 'nuovo', label: 'Nuovo', color: 'bg-blue-50 border-blue-200' },
  { id: 'messaggio_whatsapp', label: 'Messaggio WhatsApp', color: 'bg-green-50 border-green-200' },
  { id: 'telefonata_target', label: 'Telefonata Target', color: 'bg-amber-50 border-amber-200' },
  { id: 'videochiamata', label: 'Videochiamata', color: 'bg-indigo-50 border-indigo-200' },
  { id: 'convertito', label: 'Convertito', color: 'bg-emerald-50 border-emerald-200' },
  { id: 'perso', label: 'Perso', color: 'bg-slate-100 border-slate-200' }
];

export default function PipelineBoard() {
  const { token, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const notesTextareaRef = useRef(null);
  
  // Stato per il modal "Lead Perso"
  const [showLostModal, setShowLostModal] = useState(false);
  const [leadToMarkAsLost, setLeadToMarkAsLost] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [lostWhatSearching, setLostWhatSearching] = useState('');
  const [lostNotes, setLostNotes] = useState('');
  const [isRecordingLost, setIsRecordingLost] = useState(false);
  const [isTranscribingLost, setIsTranscribingLost] = useState(false);
  const [recordingTarget, setRecordingTarget] = useState(null); // 'whatSearching' o 'notes'
  const mediaRecorderLostRef = useRef(null);
  const audioChunksLostRef = useRef([]);

  // Stato per le informazioni della videochiamata
  const [videoCallInfo, setVideoCallInfo] = useState({
    obiettivi: '',
    budget: '',
    tempistiche: '',
    decisore: '',
    ostacoli: '',
    prossimi_passi: '',
    note_videochiamata: ''
  });
  const [savingVideoCall, setSavingVideoCall] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads?assigned_to=${user.email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei lead');
    } finally {
      setLoading(false);
    }
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter((lead) => lead.status === stageId);
  };

  const moveToNextStage = async (lead, nextStageId) => {
    // Se lo stato di destinazione è "perso", apri il modal
    if (nextStageId === 'perso') {
      setLeadToMarkAsLost(lead);
      setLostReason('');
      setLostWhatSearching('');
      setLostNotes('');
      setShowLostModal(true);
      return;
    }
    
    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/leads/${lead.id}`,
        { status: nextStageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead spostato con successo');
      fetchLeads();
    } catch (error) {
      toast.error('Errore nello spostamento del lead');
    } finally {
      setUpdating(false);
    }
  };

  // Funzione per confermare la perdita del lead con motivazioni
  const confirmLostLead = async () => {
    if (!leadToMarkAsLost) return;
    
    if (!lostReason) {
      toast.error('Seleziona un motivo per la perdita');
      return;
    }
    
    setUpdating(true);
    try {
      // Costruisci le note con le informazioni sulla perdita
      const timestamp = new Date().toLocaleString('it-IT');
      const lostInfo = `\n\n--- LEAD PERSO [${timestamp}] ---\nMotivo: ${lostReason}\nCosa cercava: ${lostWhatSearching || 'Non specificato'}\nNote: ${lostNotes || 'Nessuna'}`;
      
      const currentNotes = leadToMarkAsLost.notes || '';
      const updatedNotes = currentNotes + lostInfo;
      
      await axios.put(
        `${API_URL}/leads/${leadToMarkAsLost.id}`,
        { 
          status: 'perso',
          notes: updatedNotes,
          lost_reason: lostReason,
          lost_what_searching: lostWhatSearching,
          lost_notes: lostNotes,
          lost_at: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lead segnato come perso');
      setShowLostModal(false);
      setLeadToMarkAsLost(null);
      fetchLeads();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setUpdating(false);
    }
  };

  // Funzioni per la registrazione audio nel modal Lead Perso - Campo "Cosa cercava"
  const startRecordingWhatSearching = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderLostRef.current = mediaRecorder;
      audioChunksLostRef.current = [];
      setRecordingTarget('whatSearching');

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksLostRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksLostRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeForWhatSearching(audioBlob);
      };

      mediaRecorder.start();
      setIsRecordingLost(true);
      toast.info('Registrazione in corso... Parla ora!');
    } catch (error) {
      console.error('Errore accesso microfono:', error);
      toast.error('Impossibile accedere al microfono. Verifica i permessi.');
    }
  };

  const transcribeForWhatSearching = async (audioBlob) => {
    setIsTranscribingLost(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API_URL}/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.text) {
        setLostWhatSearching(prev => prev ? `${prev} ${response.data.text}` : response.data.text);
        toast.success('Audio trascritto!');
      } else {
        toast.error('Nessun testo rilevato');
      }
    } catch (error) {
      console.error('Errore trascrizione:', error);
      toast.error('Errore nella trascrizione audio');
    } finally {
      setIsTranscribingLost(false);
      setIsRecordingLost(false);
      setRecordingTarget(null);
    }
  };

  // Funzioni per la registrazione audio nel modal Lead Perso - Campo "Note aggiuntive"
  const startRecordingNotes = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderLostRef.current = mediaRecorder;
      audioChunksLostRef.current = [];
      setRecordingTarget('notes');

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksLostRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksLostRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeForNotes(audioBlob);
      };

      mediaRecorder.start();
      setIsRecordingLost(true);
      toast.info('Registrazione in corso... Parla ora!');
    } catch (error) {
      console.error('Errore accesso microfono:', error);
      toast.error('Impossibile accedere al microfono. Verifica i permessi.');
    }
  };

  const transcribeForNotes = async (audioBlob) => {
    setIsTranscribingLost(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API_URL}/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.text) {
        setLostNotes(prev => prev ? `${prev} ${response.data.text}` : response.data.text);
        toast.success('Audio trascritto!');
      } else {
        toast.error('Nessun testo rilevato');
      }
    } catch (error) {
      console.error('Errore trascrizione:', error);
      toast.error('Errore nella trascrizione audio');
    } finally {
      setIsTranscribingLost(false);
      setIsRecordingLost(false);
      setRecordingTarget(null);
    }
  };

  const stopRecordingLost = () => {
    if (mediaRecorderLostRef.current && isRecordingLost) {
      mediaRecorderLostRef.current.stop();
      setIsRecordingLost(false);
    }
  };

  // Funzione per ottenere i pulsanti disponibili per ogni stato
  const getActionButtons = (lead) => {
    switch (lead.status) {
      case 'nuovo':
        return [
          { label: 'Messaggio WhatsApp', targetStatus: 'messaggio_whatsapp', color: 'bg-green-500 hover:bg-green-600' }
        ];
      case 'messaggio_whatsapp':
        return [
          { label: 'Telefonata Target', targetStatus: 'telefonata_target', color: 'bg-amber-500 hover:bg-amber-600' }
        ];
      case 'telefonata_target':
        return [
          { label: 'In Target → Videochiamata', targetStatus: 'videochiamata', color: 'bg-indigo-600 hover:bg-indigo-700' },
          { label: 'Non in Target → Perso', targetStatus: 'perso', color: 'bg-slate-500 hover:bg-slate-600' }
        ];
      case 'videochiamata':
        return [
          { label: 'Convertito', targetStatus: 'convertito', color: 'bg-emerald-600 hover:bg-emerald-700' },
          { label: 'Perso', targetStatus: 'perso', color: 'bg-slate-500 hover:bg-slate-600' }
        ];
      default:
        return [];
    }
  };

  const updateLeadNotes = async (notes) => {
    if (!selectedLead) return;

    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/leads/${selectedLead.id}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note aggiornate con successo');
      setSelectedLead({ ...selectedLead, notes });
      fetchLeads();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento delle note');
    } finally {
      setUpdating(false);
    }
  };

  // Funzione per salvare le informazioni della videochiamata
  const saveVideoCallInfo = async () => {
    if (!selectedLead) return;

    setSavingVideoCall(true);
    try {
      await axios.put(
        `${API_URL}/leads/${selectedLead.id}`,
        { video_call_info: videoCallInfo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Informazioni videochiamata salvate!');
      setSelectedLead({ ...selectedLead, video_call_info: videoCallInfo });
      fetchLeads();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingVideoCall(false);
    }
  };

  // Carica le info videochiamata quando si seleziona un lead
  useEffect(() => {
    if (selectedLead?.video_call_info) {
      setVideoCallInfo(selectedLead.video_call_info);
    } else {
      setVideoCallInfo({
        obiettivi: '',
        budget: '',
        tempistiche: '',
        decisore: '',
        ostacoli: '',
        prossimi_passi: '',
        note_videochiamata: ''
      });
    }
  }, [selectedLead]);

  // Funzioni per la registrazione audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Registrazione in corso... Parla ora!');
    } catch (error) {
      console.error('Errore accesso microfono:', error);
      toast.error('Impossibile accedere al microfono. Verifica i permessi del browser.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API_URL}/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.text) {
        // Aggiungi il testo trascritto alle note esistenti
        const currentNotes = selectedLead.notes || '';
        const timestamp = new Date().toLocaleString('it-IT');
        const newNotes = currentNotes 
          ? `${currentNotes}\n\n[${timestamp}] ${response.data.text}`
          : `[${timestamp}] ${response.data.text}`;
        
        // Aggiorna il textarea
        if (notesTextareaRef.current) {
          notesTextareaRef.current.value = newNotes;
        }
        
        // Salva le note
        await updateLeadNotes(newNotes);
        toast.success('Audio trascritto e salvato nelle note!');
      } else {
        toast.error('Nessun testo rilevato nell\'audio');
      }
    } catch (error) {
      console.error('Errore trascrizione:', error);
      toast.error('Errore nella trascrizione audio. Riprova.');
    } finally {
      setIsTranscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="p-8 pb-4">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Pipeline</h1>
        <p className="text-slate-600">Visualizza e gestisci i lead attraverso il processo di vendita</p>
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-6 p-8 pt-4 h-full items-start" data-testid="pipeline-board">
          {PIPELINE_STAGES.map((stage) => {
            const stageLeads = getLeadsByStage(stage.id);
            return (
              <div
                key={stage.id}
                className={`flex-shrink-0 w-80 ${stage.color} backdrop-blur-sm rounded-xl border flex flex-col max-h-full`}
                data-testid={`pipeline-column-${stage.id}`}
              >
                <div className="p-4 border-b border-slate-200/60 bg-white/50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-900">{stage.label}</h3>
                    <span className="bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-2 py-1 rounded-full">
                      {stageLeads.length}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {stageLeads.length === 0 ? (
                    <div className="text-center text-slate-500 text-sm py-8">Nessun lead in questa fase</div>
                  ) : (
                    stageLeads.map((lead) => {
                      const actionButtons = getActionButtons(lead);
                      return (
                        <Card
                          key={lead.id}
                          className="bg-white p-4 rounded-lg border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                          onClick={() => setSelectedLead(lead)}
                          data-testid="lead-card"
                        >
                          <h4 className="font-semibold text-slate-900 mb-2">{lead.name}</h4>
                          <div className="space-y-1 text-xs text-slate-600 mb-3">
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              <span className="truncate">{lead.email}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              <span>{lead.phone}</span>
                            </div>
                          </div>
                          {actionButtons.length > 0 && (
                            <div className="space-y-2">
                              {actionButtons.map((btn) => (
                                <Button
                                  key={btn.targetStatus}
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveToNextStage(lead, btn.targetStatus);
                                  }}
                                  disabled={updating}
                                  className={`w-full ${btn.color} text-white text-xs h-8`}
                                  data-testid={`move-to-${btn.targetStatus}`}
                                >
                                  <MoveRight className="h-3 w-3 mr-1" />
                                  {btn.label}
                                </Button>
                              ))}
                            </div>
                          )}
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={selectedLead !== null} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Dettaglio Lead</DialogTitle>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Nome</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.name}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Email</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.email}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Telefono</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.phone}</span>
                  </div>
                </div>
              </div>

              {/* Candidatura Section */}
              {selectedLead.candidatura && Object.keys(selectedLead.candidatura).length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    📋 Risposte alla Candidatura
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                    {Object.entries(selectedLead.candidatura).map(([question, answer], index) => (
                      <div key={index} className="border-b border-slate-200 last:border-0 pb-3 last:pb-0">
                        <p className="text-sm font-semibold text-slate-700 mb-1">{question}</p>
                        <p className="text-sm text-slate-900">{answer || 'Nessuna risposta'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Box Informazioni Videochiamata - visibile solo per lead in videochiamata */}
              {selectedLead.status === 'videochiamata' && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Video className="h-5 w-5 text-indigo-600" />
                    Note Videochiamata
                  </h3>
                  <div className="bg-indigo-50 rounded-lg p-4 space-y-4">
                    <Textarea
                      value={videoCallInfo.note_videochiamata}
                      onChange={(e) => setVideoCallInfo({...videoCallInfo, note_videochiamata: e.target.value})}
                      placeholder="Inserisci qui tutte le informazioni emerse durante la videochiamata con il lead..."
                      rows={8}
                      className="bg-white resize-none"
                    />
                    <Button
                      onClick={saveVideoCallInfo}
                      disabled={savingVideoCall}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {savingVideoCall ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Salvataggio...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Save className="h-4 w-4" />
                          Salva Note
                        </div>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Note</Label>
                  <div className="flex items-center gap-2">
                    {!isRecording && !isTranscribing && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startRecording}
                        className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 h-8"
                        data-testid="start-recording-btn"
                      >
                        <Mic className="h-4 w-4" />
                        Nota Audio
                      </Button>
                    )}
                    {isRecording && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={stopRecording}
                        className="flex items-center gap-2 animate-pulse h-8"
                        data-testid="stop-recording-btn"
                      >
                        <Square className="h-4 w-4" />
                        Ferma
                      </Button>
                    )}
                    {isTranscribing && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex items-center gap-2 h-8"
                      >
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Trascrizione...
                      </Button>
                    )}
                  </div>
                </div>
                <Textarea
                  id="notes"
                  ref={notesTextareaRef}
                  defaultValue={selectedLead.notes}
                  onBlur={(e) => {
                    if (e.target.value !== selectedLead.notes) {
                      updateLeadNotes(e.target.value);
                    }
                  }}
                  placeholder="Aggiungi note sul lead... Puoi anche registrare una nota audio!"
                  rows={5}
                  className="resize-none"
                  data-testid="notes-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Clicca "Nota Audio" per registrare e trascrivere automaticamente le tue note vocali.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Lead Perso */}
      <Dialog open={showLostModal} onOpenChange={setShowLostModal}>
        <DialogContent className="max-w-lg" data-testid="lost-lead-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Segna Lead come Perso
            </DialogTitle>
          </DialogHeader>

          {leadToMarkAsLost && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Stai per segnare <strong>{leadToMarkAsLost.name}</strong> come perso. 
                Inserisci le informazioni per un'analisi futura.
              </p>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Motivo della perdita *
                </Label>
                <Select value={lostReason} onValueChange={setLostReason}>
                  <SelectTrigger className="w-full" data-testid="lost-reason-select">
                    <SelectValue placeholder="Seleziona un motivo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_in_target">Non in target</SelectItem>
                    <SelectItem value="budget_insufficiente">Budget insufficiente</SelectItem>
                    <SelectItem value="tempistiche_non_allineate">Tempistiche non allineate</SelectItem>
                    <SelectItem value="ha_scelto_competitor">Ha scelto un competitor</SelectItem>
                    <SelectItem value="non_interessato">Non più interessato</SelectItem>
                    <SelectItem value="non_raggiungibile">Non raggiungibile</SelectItem>
                    <SelectItem value="mancanza_requisiti">Mancanza requisiti</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Cosa cercava il lead?
                  </Label>
                  <div className="flex items-center gap-1">
                    {!isRecordingLost && !isTranscribingLost && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startRecordingWhatSearching}
                        className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs"
                        data-testid="record-what-searching"
                      >
                        <Mic className="h-3 w-3" />
                        Audio
                      </Button>
                    )}
                    {isRecordingLost && recordingTarget === 'whatSearching' && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={stopRecordingLost}
                        className="flex items-center gap-1 animate-pulse h-7 text-xs"
                      >
                        <Square className="h-3 w-3" />
                        Stop
                      </Button>
                    )}
                    {isTranscribingLost && recordingTarget === 'whatSearching' && (
                      <Button type="button" variant="outline" size="sm" disabled className="h-7 text-xs">
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        Trascrizione...
                      </Button>
                    )}
                  </div>
                </div>
                <Textarea
                  value={lostWhatSearching}
                  onChange={(e) => setLostWhatSearching(e.target.value)}
                  placeholder="Es: Cercava un servizio di consulenza più economico, non aveva budget sufficiente... (oppure usa il pulsante Audio)"
                  rows={4}
                  className="resize-none"
                  data-testid="lost-what-searching"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowLostModal(false)}
              data-testid="cancel-lost-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmLostLead}
              disabled={updating || !lostReason}
              className="bg-slate-600 hover:bg-slate-700 text-white"
              data-testid="confirm-lost-btn"
            >
              {updating ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvataggio...
                </div>
              ) : (
                'Conferma Lead Perso'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}